/**
 * Careers page (2026-10-08) — public API (no login) at /api/v1/public/recruit.
 * Must NOT import the authenticated app shell / stores (standalone-liftable).
 */
const API_URL = ((import.meta as any).env?.VITE_API_URL as string | undefined) || '/api/v1';
const BASE = `${API_URL}/public/recruit`;

export interface Company {
  name: string;
  logoUrl: string | null;
  tagline: string | null;
}

export interface PublicJob {
  id: string;
  slug: string;
  jobTitle: string;
  location: string;
  employmentType: string;
  vacancies: number;
  salary: { minLakh: number | null; maxLakh: number | null } | null;
  experience: { min: number | null; max: number | null } | null;
  postedAt: string | null;
  jd: { about: string; whatYouWillDo: string; whoWeAreLookingFor: string; whatWeOffer: string };
}

export class CareersError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}) as any);
  if (!res.ok || json?.success === false) {
    throw new CareersError(res.status, json?.error || 'Something went wrong. Please try again.');
  }
  return json.data as T;
}

export const fetchJobs = async (): Promise<{ company: Company; jobs: PublicJob[] }> => unwrap(await fetch(`${BASE}/jobs`));

export const fetchJob = async (slug: string): Promise<{ company: Company; job: PublicJob }> =>
  unwrap(await fetch(`${BASE}/jobs/${encodeURIComponent(slug)}`));

export interface ApplyBody {
  job: string;
  applicantName: string;
  phone: string;
  city: string;
  email?: string;
  src?: string;
  p?: string;
  website?: string; // honeypot — stays empty for people
}

export async function submitApplication(body: ApplyBody, resume?: File | null): Promise<{ status: 'applied' }> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(body)) if (v !== undefined && v !== null) fd.set(k, String(v));
  if (resume) fd.set('resume', resume);
  return unwrap(await fetch(`${BASE}/apply`, { method: 'POST', body: fd }));
}
